import { useEffect, useRef } from 'react';
import Axios from '@/shared/api/axios';
import { parseBackendDate } from '@/shared/utils/parseBackendDate';

const INITIAL_INTERVAL_MS = 3_000;
const MAX_DURATION_MS = 15 * 60_000;

/**
 * 從貼文 media 陣列抽出未 ready/error 的影片與時間錨點。
 */
export const extractPostUnreadyVideos = (media, postCreatedAt) => {
  const videos = (media ?? [])
    .filter(m => m.type === 1 && m.stream_uid && m.stream_status !== 'ready' && m.stream_status !== 'error')
    .map(m => ({ stream_uid: m.stream_uid, thumbnail_url: m.thumbnail_url }));
  const createdAt = parseBackendDate(postCreatedAt);
  return { videos, createdAt };
};

/**
 * 從聊天訊息陣列抽出未 ready/error 的影片與時間錨點（取最早未完成訊息的 created_at）。
 */
export const extractChatUnreadyVideos = (chatHistory) => {
  const videos = [];
  let earliest = null;
  for (const msg of chatHistory ?? []) {
    let hasUnready = false;
    for (const item of msg.media ?? []) {
      if (
        item.type === 'video' && item.stream_uid &&
        item.stream_status !== 'ready' && item.stream_status !== 'error'
      ) {
        videos.push({ stream_uid: item.stream_uid, thumbnail_url: item.thumbnail_url });
        hasUnready = true;
      }
    }
    if (hasUnready && msg.created_at) {
      const ts = parseBackendDate(msg.created_at);
      if (ts !== null && (earliest === null || ts < earliest)) earliest = ts;
    }
  }
  return { videos, createdAt: earliest };
};

/**
 * 從媒體列表（跨多篇貼文，如偶像頁媒體 Tab）抽出未 ready/error 的影片與時間錨點。
 * 每筆媒體各自有 created_at，取最早的一筆當輪詢起始錨點（與 extractChatUnreadyVideos 相同邏輯）。
 */
export const extractMediaUnreadyVideos = (mediaList) => {
  const videos = [];
  let earliest = null;
  for (const item of mediaList ?? []) {
    if (item.type === 1 && item.stream_uid && item.stream_status !== 'ready' && item.stream_status !== 'error') {
      videos.push({ stream_uid: item.stream_uid, thumbnail_url: item.thumbnail_url });
      const ts = parseBackendDate(item.created_at);
      if (ts !== null && (earliest === null || ts < earliest)) earliest = ts;
    }
  }
  return { videos, createdAt: earliest };
};

/**
 * 通用影片狀態輪詢。對 unreadyVideos 中所有 stream_uid 批次查詢，直到全部 ready/error 或超過 15 分鐘。
 * 輪詢間隔從 3s 開始每次雙倍，根據 createdAt 推算起始位置；createdAt 超過 15 分鐘或落在未來（排程貼文）改以當下為基準重新開始。
 * ready 的影片會先預載 thumbnail 進瀏覽器快取，再呼叫 onStatusChange，避免「狀態翻轉但縮圖晚出現」的時間差。
 *
 * @param {Object} args
 * @param {string} args.endpoint          - 後端輪詢端點
 * @param {Array}  args.unreadyVideos     - [{ stream_uid, thumbnail_url? }]
 * @param {number|null} args.createdAt    - ms timestamp
 * @param {Function} args.onStatusChange  - (changedItems) => void
 */
export function useStreamPolling({ endpoint, unreadyVideos, createdAt, onStatusChange }) {
  const videosRef = useRef(unreadyVideos);
  videosRef.current = unreadyVideos;

  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const hasUnready = unreadyVideos.length > 0;

  useEffect(() => {
    if (!hasUnready || !Number.isFinite(createdAt)) return;

    // 排程貼文的 post_created_at 是未來時間，直接當錨點會讓 elapsed 為負、第一次輪詢被排到排程時間之後
    // （影片其實在上傳當下就開始轉檔），與超過 15 分鐘一樣改以當下為基準
    const elapsedSinceCreated = Date.now() - createdAt;
    const anchorCreatedAt = (elapsedSinceCreated >= MAX_DURATION_MS || elapsedSinceCreated < 0) ? Date.now() : createdAt;

    const calcFirstDelay = () => {
      const elapsed = Date.now() - anchorCreatedAt;
      if (elapsed >= MAX_DURATION_MS) return null;
      let interval = INITIAL_INTERVAL_MS;
      let cumulative = interval;
      while (cumulative <= elapsed) {
        interval *= 2;
        cumulative += interval;
      }
      if (cumulative > MAX_DURATION_MS) return null;
      return { delay: cumulative - elapsed, interval };
    };

    const init = calcFirstDelay();
    if (!init) return;

    let currentInterval = init.interval;
    let timerId;

    const poll = async () => {
      const uids = videosRef.current.map(v => v.stream_uid);
      if (uids.length === 0) return;

      try {
        // 背景輪詢，使用者沒有主動操作：斷線時不觸發整頁 ServerErrorPage（休眠喚醒、網路未恢復時
        // 打出去會洗掉使用者正在編輯的內容），失敗沿用下方 catch 繼續下一輪
        const res = await Axios.post(endpoint, { stream_uids: uids }, { isBackground: true });
        const changed = (res.data?.data ?? []).filter(
          item => item.stream_status === 'ready' || item.stream_status === 'error'
        );

        if (!mountedRef.current) return;

        if (changed.length > 0) {
          await Promise.all(
            changed
              .filter(c => c.stream_status === 'ready')
              .map(c => {
                const v = videosRef.current.find(o => o.stream_uid === c.stream_uid);
                const thumb = c.thumbnail_url || v?.thumbnail_url;
                if (!thumb) return null;
                return new Promise(resolve => {
                  const img = new Image();
                  img.onload = img.onerror = () => resolve();
                  img.src = thumb;
                });
              })
          );

          if (!mountedRef.current) return;

          onStatusChangeRef.current(changed);

          const changedUids = new Set(changed.map(c => c.stream_uid));
          const stillUnready = videosRef.current.filter(v => !changedUids.has(v.stream_uid));
          if (stillUnready.length === 0) return;
        }
      } catch {
        if (!mountedRef.current) return;
      }

      currentInterval *= 2;
      const elapsed = Date.now() - anchorCreatedAt;
      if (elapsed + currentInterval > MAX_DURATION_MS) return;

      timerId = setTimeout(poll, currentInterval);
    };

    timerId = setTimeout(poll, init.delay);
    return () => clearTimeout(timerId);
  }, [hasUnready, createdAt, endpoint]);
}
